/*
 * Sign template customizer — personalization PDP for products created from a
 * dashboard design template (template_suffix: sign-template).
 *
 * The template JSON (background + text slots with designer-set limits) is
 * embedded in the page from the product's custom.design_template metafield.
 * Customers edit text per slot via form fields — no canvas dragging — and the
 * browser renders the print file at add-to-cart, exactly like the
 * upload-design product (same proof modal flow, same _ai-design-* line item
 * properties, so cart/fulfillment work unchanged).
 *
 * Text painting is ported from upload-design-generator.js paintText()/
 * outlineColorFor() — keep in sync.
 */
(function () {
  var container = document.getElementById('sign-customizer');
  if (!container) return;

  var dataEl = document.getElementById('sign-template-data');
  var template = null;
  try {
    template = JSON.parse(dataEl.textContent);
  } catch (e) { /* fall through */ }
  if (!template || !template.background || !(template.slots || []).length) {
    container.hidden = true;
    return;
  }

  var API_ENDPOINT = container.dataset.apiEndpoint;
  var API_KEY = container.dataset.apiKey || '';

  // Canvas + slot cards live in the product gallery (sign-template branch of
  // blocks/_product-gallery.liquid); this script just finds them by id.
  var canvas = document.getElementById('sign-customizer-canvas');
  var slotsWrap = document.getElementById('sign-slots');
  if (!canvas || !slotsWrap) return;
  var ctx = canvas.getContext('2d');
  var errorEl = document.getElementById('sign-error');
  var previewModal = document.getElementById('sign-preview-modal');
  var previewImg = document.getElementById('sign-preview-img');
  var previewApprove = document.getElementById('sign-preview-approve');
  var previewCancel = document.getElementById('sign-preview-cancel');
  var previewClose = document.getElementById('sign-preview-close');
  var previewCheckbox = document.getElementById('sign-preview-checkbox');

  // Preview canvas working cap — big enough to look sharp, cheap on phones.
  var PREVIEW_MAX = 1400;
  // Print export cap, matching upload-design MAX_CROP (backend upscales to
  // 5400x7088 and rotates; iOS canvas memory is the constraint).
  var MAX_EXPORT = 5400;

  var bgPreviewImg = null;
  var uploadInProgress = false;
  var _approved = false;

  // Customer state per slot: { text, fontFamily, color, sizePct }
  var values = {};
  template.slots.forEach(function (s) {
    values[s.id] = { text: '', fontFamily: s.fonts[0], color: s.colors[0], sizePct: s.sizePct };
  });

  // --- Fonts: load only the families this template allows ---
  var usedFamilies = [];
  template.slots.forEach(function (s) {
    s.fonts.forEach(function (f) {
      if (usedFamilies.indexOf(f) === -1) usedFamilies.push(f);
    });
  });
  var fontsRequested = false;
  function ensureFonts() {
    if (fontsRequested) return;
    fontsRequested = true;
    var families = usedFamilies.map(function (f) { return f.replace(/ /g, '+'); }).join('&family=');
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=' + families + '&display=swap';
    link.onload = function () {
      if (document.fonts) {
        usedFamilies.forEach(function (fam) {
          document.fonts.load('64px "' + fam + '"').then(draw).catch(function () {});
        });
      }
    };
    document.head.appendChild(link);
  }
  // Every family must be resolved before the export renders — text IS the
  // product here, a fallback-font print file is a defect.
  function awaitFonts() {
    ensureFonts();
    if (!document.fonts) return Promise.resolve();
    return Promise.all(usedFamilies.map(function (fam) {
      return document.fonts.load('64px "' + fam + '"').catch(function () {});
    })).then(function () { return document.fonts.ready; }).catch(function () {});
  }

  // --- Painting (ported from upload-design-generator.js; keep in sync) ---

  function outlineColorFor(hex) {
    var c = hex.replace('#', '');
    var r = parseInt(c.slice(0, 2), 16) || 0;
    var g = parseInt(c.slice(2, 4), 16) || 0;
    var b = parseInt(c.slice(4, 6), 16) || 0;
    var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? '#111111' : '#ffffff';
  }

  // Mirrors customOrderEditorEngine.ts paintText (stroke/shadow/curve) — keep
  // the two painters in sync.
  function paintText(targetCtx, t, ox, oy, uw, uh) {
    var fontPx = Math.max(1, t.sizePct * uh);
    var tcx = ox + t.xPct * uw;
    var tcy = oy + t.yPct * uh;
    targetCtx.save();
    targetCtx.translate(tcx, tcy);
    targetCtx.rotate(t.rotation * Math.PI / 180);
    targetCtx.font = fontPx + 'px "' + t.fontFamily + '", sans-serif';
    targetCtx.textBaseline = 'middle';

    var strokeWidth = t.stroke
      ? Math.max(0, t.stroke.widthPct * fontPx)
      : t.outline ? Math.max(1, fontPx * 0.14) : 0;
    var strokeColor = t.stroke ? t.stroke.color : outlineColorFor(t.color);

    function paintRun(text, x, y) {
      if (t.shadow) {
        targetCtx.shadowColor = t.shadow.color;
        targetCtx.shadowBlur = t.shadow.blurPct * fontPx;
        targetCtx.shadowOffsetX = t.shadow.offsetXPct * fontPx;
        targetCtx.shadowOffsetY = t.shadow.offsetYPct * fontPx;
      }
      if (strokeWidth > 0) {
        targetCtx.lineWidth = strokeWidth;
        targetCtx.strokeStyle = strokeColor;
        targetCtx.lineJoin = 'round';
        targetCtx.strokeText(text, x, y);
        targetCtx.shadowColor = 'rgba(0,0,0,0)';
      }
      targetCtx.fillStyle = t.color;
      targetCtx.fillText(text, x, y);
      targetCtx.shadowColor = 'rgba(0,0,0,0)';
    }

    var lines = t.text.split('\n');
    var lh = fontPx * 1.2;
    var startY = -((lines.length - 1) * lh) / 2;
    var bend = (t.curve || 0) / 100;

    for (var i = 0; i < lines.length; i++) {
      var y = startY + i * lh;
      var line = lines[i];

      if (!bend || !line.trim()) {
        targetCtx.textAlign = 'center';
        paintRun(line, 0, y);
        continue;
      }

      // Curved: each character placed along an arc; |bend|=1 = half circle.
      targetCtx.textAlign = 'center';
      var widths = [];
      var lineW = 0;
      var chars = line.split('');
      for (var c = 0; c < chars.length; c++) {
        var w = targetCtx.measureText(chars[c]).width;
        widths.push(w);
        lineW += w;
      }
      if (lineW <= 0) continue;
      var theta = Math.abs(bend) * Math.PI;
      var radius = lineW / theta;
      var dir = bend > 0 ? 1 : -1;

      var cursor = 0;
      for (var k = 0; k < chars.length; k++) {
        var charW = widths[k];
        var alpha = ((cursor + charW / 2 - lineW / 2) / radius) * dir;
        var px = radius * Math.sin(alpha) * dir;
        var py = y + dir * radius * (1 - Math.cos(alpha));
        targetCtx.save();
        targetCtx.translate(px, py);
        targetCtx.rotate(alpha);
        paintRun(chars[k], 0, 0);
        targetCtx.restore();
        cursor += charW;
      }
    }
    targetCtx.restore();
  }

  // --- Shrink-to-fit ---
  // Text may never render wider than the slot's lane: the designer's explicit
  // maxWidthPct, or (auto) twice the distance from the anchor to the nearest
  // horizontal sign edge minus a margin. Returns the effective sizePct; the
  // fitted value flows into the preview, the export, AND the stored design
  // snapshot, so every later re-render matches the print without knowing the
  // fitting rule. Ratio is scale-invariant, so measuring on the preview canvas
  // is valid for the export too.
  function slotMaxWidthPct(slot) {
    if (slot.maxWidthPct > 0) return slot.maxWidthPct;
    var lane = 2 * Math.min(slot.xPct, 1 - slot.xPct) - 0.04;
    return Math.max(0.1, lane);
  }

  function fittedSizePct(slot, text, fontFamily, sizePct) {
    if (!text || !canvas.width) return sizePct;
    var maxW = slotMaxWidthPct(slot) * canvas.width;
    var fontPx = Math.max(1, sizePct * canvas.height);
    ctx.save();
    ctx.font = fontPx + 'px "' + fontFamily + '", sans-serif';
    var lines = text.split('\n');
    var widest = 0;
    for (var i = 0; i < lines.length; i++) {
      widest = Math.max(widest, ctx.measureText(lines[i] || ' ').width);
    }
    ctx.restore();
    if (widest <= maxW || widest === 0) return sizePct;
    return Math.max(0.03, sizePct * (maxW / widest));
  }

  // A slot's paintable layer: the customer's value, or the designer's default
  // text when empty (preview shows it ghosted; export skips optional empties).
  function slotLayer(slot, forExport) {
    var v = values[slot.id];
    var text = v.text.trim() ? v.text : slot.defaultText;
    if (forExport && !v.text.trim()) {
      // Required slots can't reach export empty (gated); optional ones fall
      // back to the designer's default text so the sign never prints a hole.
      text = slot.defaultText;
    }
    if (slot.forceUppercase) text = text.toUpperCase();
    return {
      text: text,
      xPct: slot.xPct,
      yPct: slot.yPct,
      sizePct: fittedSizePct(slot, text, v.fontFamily, v.sizePct),
      rotation: slot.rotation,
      fontFamily: v.fontFamily,
      color: v.color,
      align: slot.align,
      outline: slot.outline,
      stroke: slot.stroke,
      shadow: slot.shadow,
      curve: slot.curve,
      isPlaceholder: !v.text.trim(),
    };
  }

  function draw() {
    if (!bgPreviewImg) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bgPreviewImg, 0, 0, canvas.width, canvas.height);
    template.slots.forEach(function (slot) {
      var layer = slotLayer(slot, false);
      if (layer.isPlaceholder) ctx.globalAlpha = 0.65;
      paintText(ctx, layer, 0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
    });
  }
  // Gallery hook: redraw when flickity settles back on the canvas slide.
  window.signCustomizerRedraw = draw;

  function loadBackground(url, cb, errCb) {
    var img = new Image();
    img.crossOrigin = 'anonymous'; // imagedelivery.net sends ACAO: * — no taint
    img.onload = function () { cb(img); };
    img.onerror = function () { errCb(); };
    img.src = url;
  }

  // Flickity measures cells at init, before the canvas has its real size —
  // without a re-measure the slides below overlap the image.
  function refreshGallerySize() {
    var gallery = canvas.closest('.flickity-gallery');
    if (!gallery || !window.Flickity) return;
    var flkty = Flickity.data(gallery);
    if (flkty) flkty.resize();
  }

  loadBackground(template.background.previewUrl, function (img) {
    bgPreviewImg = img;
    var scale = Math.min(1, PREVIEW_MAX / Math.max(img.naturalWidth, img.naturalHeight));
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    ensureFonts();
    draw();
    updateGate();
    refreshGallerySize();
    // Flickity may initialize after us on slow loads — re-measure again shortly.
    setTimeout(refreshGallerySize, 500);
    setTimeout(refreshGallerySize, 1500);
  }, function () {
    showError('Could not load the sign preview. Please refresh the page.');
  });

  // Glow the inputs until the customer first interacts (parity with the
  // Customily options treatment in main-product.liquid / app-customily.css).
  ['pointerdown', 'focusin'].forEach(function (evt) {
    slotsWrap.addEventListener(evt, function () {
      var wrap = slotsWrap.closest('.sign-options-container');
      if (wrap) wrap.classList.add('user-interacted');
    }, { once: true });
  });

  // --- Slot form cards ---

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }
  function hideError() {
    errorEl.hidden = true;
  }

  function clampLines(text, maxLines, maxChars) {
    var lines = text.split('\n').slice(0, maxLines);
    return lines.map(function (l) { return l.slice(0, maxChars); }).join('\n');
  }

  function buildSlotCard(slot) {
    var card = document.createElement('div');
    card.className = 'sign-slot';

    var label = document.createElement('label');
    label.className = 'sign-slot__label';
    label.textContent = slot.label + (slot.required ? '' : ' (optional)');
    card.appendChild(label);

    var input = document.createElement(slot.maxLines > 1 ? 'textarea' : 'input');
    if (slot.maxLines > 1) {
      input.rows = slot.maxLines;
    } else {
      input.type = 'text';
    }
    input.className = 'sign-slot__input';
    input.placeholder = slot.defaultText;
    input.setAttribute('maxlength', String(slot.maxChars * slot.maxLines + slot.maxLines));
    input.addEventListener('input', function () {
      values[slot.id].text = clampLines(input.value, slot.maxLines, slot.maxChars);
      if (input.value !== values[slot.id].text) input.value = values[slot.id].text;
      hideError();
      draw();
      updateGate();
    });
    input.addEventListener('focus', ensureFonts);
    label.appendChild(input);

    // Font picker — only when the designer allows a choice. Chips read well up
    // to a handful; larger curated lists get a dropdown with each option
    // rendered in its own typeface.
    if (slot.fonts.length > 1 && slot.fonts.length <= 6) {
      var fontsRow = document.createElement('div');
      fontsRow.className = 'sign-slot__fonts';
      slot.fonts.forEach(function (fam) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'sign-slot__font' + (fam === values[slot.id].fontFamily ? ' is-active' : '');
        chip.textContent = fam;
        chip.style.fontFamily = '"' + fam + '", sans-serif';
        chip.addEventListener('click', function () {
          values[slot.id].fontFamily = fam;
          fontsRow.querySelectorAll('.sign-slot__font').forEach(function (b) { b.classList.remove('is-active'); });
          chip.classList.add('is-active');
          draw();
        });
        fontsRow.appendChild(chip);
      });
      card.appendChild(fontsRow);
    } else if (slot.fonts.length > 6) {
      var dd = document.createElement('div');
      dd.className = 'sign-slot__font-dd';
      var ddBtn = document.createElement('button');
      ddBtn.type = 'button';
      ddBtn.className = 'sign-slot__font-dd-btn';
      function setDdLabel() {
        ddBtn.innerHTML = '';
        var span = document.createElement('span');
        span.textContent = values[slot.id].fontFamily;
        span.style.fontFamily = '"' + values[slot.id].fontFamily + '", sans-serif';
        ddBtn.appendChild(span);
        var caret = document.createElement('span');
        caret.className = 'sign-slot__font-dd-caret';
        caret.textContent = '▾';
        ddBtn.appendChild(caret);
      }
      setDdLabel();
      var ddList = document.createElement('div');
      ddList.className = 'sign-slot__font-dd-list';
      ddList.hidden = true;
      slot.fonts.forEach(function (fam) {
        var opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'sign-slot__font-dd-opt';
        opt.textContent = fam;
        opt.style.fontFamily = '"' + fam + '", sans-serif';
        opt.addEventListener('click', function () {
          values[slot.id].fontFamily = fam;
          setDdLabel();
          ddList.hidden = true;
          draw();
        });
        ddList.appendChild(opt);
      });
      ddBtn.addEventListener('click', function () {
        ddList.hidden = !ddList.hidden;
      });
      document.addEventListener('click', function (e) {
        if (!dd.contains(e.target)) ddList.hidden = true;
      });
      dd.appendChild(ddBtn);
      dd.appendChild(ddList);
      card.appendChild(dd);
    }

    // Color swatches — only when the designer offers a palette
    if (slot.colors.length > 1) {
      var colorsRow = document.createElement('div');
      colorsRow.className = 'sign-slot__colors';
      slot.colors.forEach(function (hex) {
        var sw = document.createElement('button');
        sw.type = 'button';
        sw.className = 'sign-slot__color' + (hex === values[slot.id].color ? ' is-active' : '');
        sw.style.backgroundColor = hex;
        sw.setAttribute('aria-label', 'Text color ' + hex);
        sw.addEventListener('click', function () {
          values[slot.id].color = hex;
          colorsRow.querySelectorAll('.sign-slot__color').forEach(function (b) { b.classList.remove('is-active'); });
          sw.classList.add('is-active');
          draw();
        });
        colorsRow.appendChild(sw);
      });
      card.appendChild(colorsRow);
    }

    // Size slider — only when the designer allows resizing
    if (slot.sizeRange.min < slot.sizeRange.max) {
      var sizeLabel = document.createElement('label');
      sizeLabel.className = 'sign-slot__size-label';
      sizeLabel.textContent = 'Text size';
      var slider = document.createElement('input');
      slider.type = 'range';
      slider.min = String(Math.round(slot.sizeRange.min * 1000));
      slider.max = String(Math.round(slot.sizeRange.max * 1000));
      slider.value = String(Math.round(slot.sizePct * 1000));
      slider.addEventListener('input', function () {
        values[slot.id].sizePct = Number(slider.value) / 1000;
        draw();
      });
      sizeLabel.appendChild(slider);
      card.appendChild(sizeLabel);
    }

    return card;
  }

  template.slots.forEach(function (slot) {
    slotsWrap.appendChild(buildSlotCard(slot));
  });

  // --- Add-to-cart gate + intercept (same pattern as upload-design) ---

  function requiredFilled() {
    return template.slots.every(function (s) {
      return !s.required || values[s.id].text.trim().length > 0;
    });
  }

  function getAddToCartBtn() {
    return document.querySelector('[data-main-product-add-to-cart], product-form button[type="submit"]');
  }

  function updateGate() {
    var enabled = !!bgPreviewImg && requiredFilled();
    document.querySelectorAll('[data-main-product-add-to-cart][data-requires-design]').forEach(function (b) {
      b.disabled = !enabled;
      b.classList.toggle('disabled', !enabled);
    });
  }

  function firstEmptyRequired() {
    for (var i = 0; i < template.slots.length; i++) {
      var s = template.slots[i];
      if (s.required && !values[s.id].text.trim()) return s;
    }
    return null;
  }

  // Render the print export: high-res background + text at export resolution.
  function buildExport(cb, errCb) {
    loadBackground(template.background.productionUrl, function (bigImg) {
      try {
        var w = bigImg.naturalWidth;
        var h = bigImg.naturalHeight;
        var scale = Math.min(1, MAX_EXPORT / Math.max(w, h));
        var exportCanvas = document.createElement('canvas');
        exportCanvas.width = Math.round(w * scale);
        exportCanvas.height = Math.round(h * scale);
        var exCtx = exportCanvas.getContext('2d');
        exCtx.drawImage(bigImg, 0, 0, exportCanvas.width, exportCanvas.height);
        template.slots.forEach(function (slot) {
          paintText(exCtx, slotLayer(slot, true), 0, 0, exportCanvas.width, exportCanvas.height);
        });
        cb(exportCanvas.toDataURL('image/jpeg', 0.92));
      } catch (err) {
        errCb(err);
      }
    }, function () {
      errCb(new Error('Could not load the full-resolution background.'));
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-main-product-add-to-cart], product-form button[type="submit"]');
    if (!btn) return;
    if (_approved) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var missing = firstEmptyRequired();
    if (missing) {
      showError('Please fill in "' + missing.label + '" before adding to cart.');
      slotsWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (previewCheckbox) previewCheckbox.checked = false;
    if (previewApprove) previewApprove.disabled = true;
    previewImg.src = '';
    previewModal.hidden = false;
    previewModal._exportJpeg = null;

    awaitFonts().then(function () {
      buildExport(function (jpeg) {
        previewModal._exportJpeg = jpeg;
        previewImg.src = jpeg;
        if (previewCheckbox && previewCheckbox.checked && previewApprove) previewApprove.disabled = false;
      }, function () {
        previewModal.hidden = true;
        showError('Could not prepare your design. Please try again.');
      });
    });
  }, true);

  if (previewCheckbox) {
    previewCheckbox.addEventListener('change', function () {
      previewApprove.disabled = !(previewCheckbox.checked && previewModal._exportJpeg);
    });
  }

  if (previewApprove) {
    previewApprove.addEventListener('click', async function () {
      if (uploadInProgress || !previewModal._exportJpeg) return;
      uploadInProgress = true;
      previewApprove.disabled = true;
      previewApprove.textContent = 'Uploading…';

      try {
        var design = {
          templateId: template.id,
          templateVersion: template.version,
          background: template.background,
          values: template.slots.map(function (slot) {
            var layer = slotLayer(slot, true);
            return {
              slotId: slot.id,
              text: layer.text,
              fontFamily: layer.fontFamily,
              color: layer.color,
              xPct: layer.xPct,
              yPct: layer.yPct,
              sizePct: layer.sizePct,
              rotation: layer.rotation,
              align: layer.align,
              outline: layer.outline,
              stroke: layer.stroke,
              shadow: layer.shadow,
              curve: layer.curve,
            };
          }),
        };

        var res = await fetch(API_ENDPOINT + '/template-design', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
          body: JSON.stringify({ imageData: previewModal._exportJpeg, design: design }),
        });
        var data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');

        // Same keys as AI design so cart + fulfillment work unchanged, plus
        // the design id for staff lookup.
        var formEl = document.querySelector('product-form form');
        if (formEl) {
          formEl.querySelectorAll('input[name^="properties[_ai-design"], input[name="properties[_design-id]"]').forEach(function (el) { el.remove(); });
          [
            ['properties[_ai-design-production-url]', data.productionUrl],
            ['properties[_ai-design-preview]', data.previewUrl],
            ['properties[_design-id]', data.designId],
          ].forEach(function (pair) {
            var input = document.createElement('input');
            input.type = 'hidden';
            input.name = pair[0];
            input.value = pair[1];
            formEl.appendChild(input);
          });
        }

        previewModal.hidden = true;
        _approved = true;
        var realBtn = getAddToCartBtn();
        if (realBtn) { realBtn.disabled = false; realBtn.click(); }
        setTimeout(function () { _approved = false; }, 500);
      } catch (err) {
        showError('Upload failed: ' + err.message);
        previewModal.hidden = true;
      } finally {
        uploadInProgress = false;
        previewApprove.disabled = false;
        previewApprove.textContent = 'Add to Cart';
      }
    });
  }

  function closePreview() {
    if (uploadInProgress) return;
    previewModal.hidden = true;
    previewApprove.disabled = true;
    previewApprove.textContent = 'Add to Cart';
    if (previewCheckbox) previewCheckbox.checked = false;
  }
  if (previewCancel) previewCancel.addEventListener('click', closePreview);
  if (previewClose) previewClose.addEventListener('click', closePreview);

  updateGate();
})();
